import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { submitTranslatorApplication } from "@/lib/apiClient";
import { Languages, Check, ChevronDown, ArrowRight, LayoutDashboard } from "lucide-react";

const PROFICIENCIES = ["native", "fluent", "advanced", "intermediate"];

// Same field styling as Onboarding's fieldCls — reused verbatim so this
// form reads as part of the same app, not a bolted-on page.
const fieldCls = "w-full px-4 py-3 rounded-xl border border-[#E7E0D2] bg-white text-sm text-[#1F2937] focus:outline-none focus:border-[#064E3B] transition placeholder:text-[#9CA3AF]";
const labelCls = "text-sm font-medium text-[#1F2937] mb-1.5 block";

export default function ApplyTranslator() {
  const { user } = useAuth();
  const { t, languages } = useI18n();
  const nav = useNavigate();

  const [fullName, setFullName] = useState(user?.name || "");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [targetLanguage, setTargetLanguage] = useState("");
  const [proficiency, setProficiency] = useState(null);
  const [hasExperience, setHasExperience] = useState(null); // null = unanswered, true/false once chosen
  const [experienceDesc, setExperienceDesc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // "Which language would you like to translate into" is populated from
  // the exact same published-locale list the header's language switcher
  // and Settings' LanguagePicker already use (useI18n().languages, backed
  // by GET /i18n/locales) — not a hardcoded list — so a language admins
  // publish or unpublish is reflected here automatically, no code change.
  // English itself isn't a valid target: applicants translate FROM
  // English, so it's excluded from the "translate into" choices.
  const languageOptions = languages.filter((l) => l.code !== "en");

  const isValid =
    fullName.trim() && mobile.trim() && email.trim() && targetLanguage && proficiency && hasExperience !== null;

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid || submitting) return; // guards against a double-click submitting twice
    setSubmitting(true);
    try {
      await submitTranslatorApplication({
        full_name: fullName.trim(),
        mobile_number: mobile.trim(),
        email: email.trim(),
        target_language: targetLanguage,
        proficiency,
        has_experience: hasExperience,
        experience_description: hasExperience ? experienceDesc.trim() || null : null,
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t("translator_apply.toast_generic_error"));
      // Entered data deliberately isn't cleared here — a failed submit
      // (network blip, a duplicate-application 409) shouldn't cost the
      // applicant everything they just typed.
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col" data-testid="apply-translator-success-page">
        <Header />
        <main className="flex-1 max-w-[640px] mx-auto w-full px-5 sm:px-8 py-20 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-full bg-[#ECFDF5] border border-[#A7F3D0] grid place-items-center">
            <Check className="w-7 h-7 text-[#064E3B]" strokeWidth={2.5} />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold mt-6 text-[#1F2937]" data-testid="apply-translator-success-title">
            {t("translator_apply.success_title")}
          </h1>
          <p className="text-[#4B5563] mt-3 leading-relaxed max-w-md">{t("translator_apply.success_body")}</p>
          <button
            data-testid="apply-translator-success-cta"
            onClick={() => nav("/dashboard")}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white font-medium transition">
            <LayoutDashboard className="w-4 h-4" /> {t("translator_apply.success_cta")}
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" data-testid="apply-translator-page">
      <Header />
      <main className="flex-1 max-w-[640px] mx-auto w-full px-5 sm:px-8 py-12">
        <div className="w-11 h-11 rounded-2xl bg-[#ECFDF5] text-[#064E3B] flex items-center justify-center">
          <Languages className="w-5 h-5" />
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-4 text-[#1F2937]">
          {t("translator_apply.title")}
        </h1>
        <p className="mt-2 text-[#4B5563] leading-relaxed">{t("translator_apply.subtitle")}</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="apply-translator-form">
          <div>
            <label className={labelCls} htmlFor="ta-full-name">{t("translator_apply.full_name_label")}</label>
            <input id="ta-full-name" data-testid="apply-translator-full-name" required
              value={fullName} onChange={(e) => setFullName(e.target.value)}
              placeholder={t("translator_apply.full_name_placeholder")} className={fieldCls} />
          </div>

          <div>
            <label className={labelCls} htmlFor="ta-mobile">{t("translator_apply.mobile_label")}</label>
            <input id="ta-mobile" data-testid="apply-translator-mobile" required type="tel"
              value={mobile} onChange={(e) => setMobile(e.target.value)}
              placeholder={t("translator_apply.mobile_placeholder")} className={fieldCls} />
          </div>

          <div>
            <label className={labelCls} htmlFor="ta-email">{t("translator_apply.email_label")}</label>
            <input id="ta-email" data-testid="apply-translator-email" required type="email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder={t("translator_apply.email_placeholder")} className={fieldCls} />
          </div>

          <div>
            <label className={labelCls} htmlFor="ta-language">{t("translator_apply.language_label")}</label>
            <div className="relative">
              <select id="ta-language" data-testid="apply-translator-language" required
                value={targetLanguage} onChange={(e) => setTargetLanguage(e.target.value)}
                className={`${fieldCls} appearance-none pr-9`}>
                <option value="" disabled>{t("translator_apply.language_placeholder")}</option>
                {languageOptions.map((l) => (
                  <option key={l.code} value={l.code}>{l.native} · {l.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
            </div>
            <p className="mt-1.5 text-xs text-[#4B5563]">{t("translator_apply.language_hint")}</p>
          </div>

          <div>
            <label className={labelCls}>{t("translator_apply.proficiency_label")}</label>
            <div className="grid grid-cols-2 gap-2">
              {PROFICIENCIES.map((p) => (
                <button key={p} type="button" data-testid={`apply-translator-proficiency-${p}`}
                  onClick={() => setProficiency(p)}
                  className={`px-4 py-2.5 rounded-xl border-2 text-sm font-medium text-left transition ${
                    proficiency === p
                      ? "bg-[#064E3B] text-white border-[#064E3B]"
                      : "bg-white text-[#1F2937] border-[#E7E0D2] hover:border-[#064E3B]"
                  }`}>
                  {t(`translator_apply.proficiency_${p}`)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("translator_apply.experience_label")}</label>
            <div className="flex gap-2">
              {[["yes", true], ["no", false]].map(([key, val]) => (
                <button key={key} type="button" data-testid={`apply-translator-experience-${key}`}
                  onClick={() => { setHasExperience(val); if (!val) setExperienceDesc(""); }}
                  className={`px-5 py-2.5 rounded-full border-2 text-sm font-medium transition ${
                    hasExperience === val
                      ? "bg-[#064E3B] text-white border-[#064E3B]"
                      : "bg-white text-[#1F2937] border-[#E7E0D2] hover:border-[#064E3B]"
                  }`}>
                  {t(`translator_apply.experience_${key}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Only shown once "Yes" is picked — not required either way, so
              staying hidden (rather than just disabled) when the answer is
              "No" keeps the form from asking for something that doesn't apply. */}
          {hasExperience && (
            <div>
              <label className={labelCls} htmlFor="ta-experience-desc">{t("translator_apply.experience_desc_label")}</label>
              <textarea id="ta-experience-desc" data-testid="apply-translator-experience-desc" rows={4}
                value={experienceDesc} onChange={(e) => setExperienceDesc(e.target.value)}
                placeholder={t("translator_apply.experience_desc_placeholder")}
                className={`${fieldCls} resize-none`} />
            </div>
          )}

          <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E0D2] px-4 py-3">
            <p className="text-xs text-[#4B5563] leading-relaxed">{t("translator_apply.volunteer_notice")}</p>
          </div>

          <button type="submit" data-testid="apply-translator-submit" disabled={!isValid || submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-40 disabled:cursor-not-allowed text-white font-medium transition">
            {submitting ? t("translator_apply.submitting") : (
              <>{t("translator_apply.submit_btn")} <ArrowRight className="w-4 h-4" /></>
            )}
          </button>
        </form>
      </main>
      <Footer />
    </div>
  );
}
